/** Shape of the flag JSON value: {"startDate": "dd/mm/yyyy", "endDate": "dd/mm/yyyy", "products": ["id1", "id2"]} */
export interface BlockProductsFlagValue {
  startDate: string; // dd/mm/yyyy format
  endDate: string; // dd/mm/yyyy format
  products: string[];
}

export interface BlockableProduct {
  id: string;
}

export interface ProductBlockPolicyInput {
  products: BlockableProduct[];
  deliveryDate: string | undefined | null;
  isFeatureEnabled: boolean;
  flagValue: string | undefined | null;
}

/**
 * Safely parses the flag JSON value.
 * Returns null if value is missing, empty, or malformed.
 */
export function parseFlagValue(
  raw: string | undefined | null
): BlockProductsFlagValue | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === "object" &&
      typeof parsed.startDate === "string" &&
      typeof parsed.endDate === "string" &&
      Array.isArray(parsed.products)
    ) {
      return parsed as BlockProductsFlagValue;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Converts a date from dd/mm/yyyy to yyyy-mm-dd format.
 * Returns null if the input doesn't match the expected pattern.
 */
export function convertDateToISO(ddmmyyyy: string): string | null {
  const parts = ddmmyyyy.split("/");
  if (parts.length !== 3) return null;
  const [day, month, year] = parts;
  if (!day || !month || !year) return null;
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
}

/**
 * Extracts the blocked dates from the flag value as an array of Date objects.
 * Returns undefined if the flag is disabled, value is malformed, or dates are invalid.
 * Useful for disabling specific dates in calendar components via excludeDates.
 */
export function getBlockedDates(
  isFeatureEnabled: boolean,
  flagValue: string | undefined | null
): Date[] | undefined {
  if (!isFeatureEnabled) return undefined;

  const config = parseFlagValue(flagValue);
  if (!config) return undefined;

  const startISO = convertDateToISO(config.startDate);
  const endISO = convertDateToISO(config.endDate);
  if (!startISO || !endISO) return undefined;

  // Parse as local dates (noon to avoid DST issues)
  const startParts = startISO.split("-").map(Number);
  const endParts = endISO.split("-").map(Number);

  const start = new Date(startParts[0], startParts[1] - 1, startParts[2]);
  const end = new Date(endParts[0], endParts[1] - 1, endParts[2]);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) return undefined;
  if (start > end) return undefined;

  const dates: Date[] = [];
  const current = new Date(start);
  while (current <= end) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }

  return dates.length > 0 ? dates : undefined;
}

/**
 * Filters products based on the date-block policy.
 *
 * Returns the full product list unless ALL conditions are met:
 * 1. Feature flag is enabled
 * 2. Flag value is valid JSON with startDate, endDate, and products
 * 3. Delivery date falls within the [startDate, endDate] range (inclusive)
 * 4. Product ID is in the blocked products list
 */
export function filterBlockedProducts<T extends BlockableProduct>(
  input: ProductBlockPolicyInput & { products: T[] }
): T[] {
  const { products, deliveryDate, isFeatureEnabled, flagValue } = input;

  if (!isFeatureEnabled) return products;
  if (!deliveryDate) return products;

  const config = parseFlagValue(flagValue);
  if (!config) return products;

  const startDateISO = convertDateToISO(config.startDate);
  const endDateISO = convertDateToISO(config.endDate);
  if (!startDateISO || !endDateISO) return products;

  const normalizedDate = deliveryDate.slice(0, 10); // ensure yyyy-mm-dd
  if (normalizedDate < startDateISO || normalizedDate > endDateISO)
    return products;

  const blockedSet = new Set(config.products);
  return products.filter((p) => !blockedSet.has(p.id)) as T[];
}
